import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { listRolesRequest } from '@/services/roles';
import type { PaginatedResult, RoleListItem } from '@/types/roles';

const PAGE_SIZE = 20;

type StatusFilter = 'active' | 'inactive' | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return s === 'active' || s === 'inactive' || s === 'all';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function RolesListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'active';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);

  // 300ms debounce for search input.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  // Sync URL search params when state changes.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'active') next.set('status', status);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, page, setSearchParams]);

  // Reset to page 1 when query/status changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status]);

  const query = useQuery<PaginatedResult<RoleListItem>, ApiError>({
    queryKey: ['roles', debouncedQ, status, page, PAGE_SIZE],
    queryFn: () => listRolesRequest({ q: debouncedQ, status, page, pageSize: PAGE_SIZE }),
  });

  const columns = useMemo<ColumnDef<RoleListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Role Name',
        cell: ({ row }) => (
          <Link
            to={`/roles/${String(row.original.id)}`}
            className="text-primary hover:underline"
            data-testid={`roles-row-name-${String(row.original.id)}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description ?? '—',
      },
      {
        accessorKey: 'usersCount',
        header: 'Users Count',
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created Date',
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
    ],
    [],
  );

  const data = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Cast through unknown to satisfy readonly→mutable boundary required by
  // TanStack Table v8 without sacrificing element typing.
  const tableData = data as unknown as RoleListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('roles.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('roles.create');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Roles</h1>
          <p className="text-muted-foreground">Manage roles and their permissions.</p>
        </div>
        {canCreate && (
          <Button asChild data-testid="roles-add-button">
            <Link to="/roles/new">
              <PlusIcon size={16} weight="bold" /> Add Role
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Roles</CardTitle>
          <CardDescription>Search by name or filter by status.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="roles-search">Search</Label>
              <Input
                id="roles-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                placeholder="Filter by role name…"
                data-testid="roles-search-input"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="roles-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isValidStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger id="roles-status" data-testid="roles-status-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" data-testid="roles-status-active">
                    Active
                  </SelectItem>
                  <SelectItem value="inactive" data-testid="roles-status-inactive">
                    Inactive
                  </SelectItem>
                  <SelectItem value="all" data-testid="roles-status-all">
                    All
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {query.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="roles-loading">
              Loading roles…
            </p>
          )}
          {query.isError && (
            <p className="text-sm text-destructive" data-testid="roles-error">
              Failed to load roles.
            </p>
          )}
          {query.data && (
            <>
              <div className="overflow-x-auto">
              <Table data-testid="roles-table">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-8">
                        <span data-testid="roles-empty">No roles found.</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} data-testid={`roles-row-${String(row.original.id)}`}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground" data-testid="roles-pagination-summary">
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="roles-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="roles-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
